// The kiosk's top-up amounts and extras, in Settings → Money → Wallets (spec/13 §13; F-WAL-03, F-KIO-10; 2026-10-01).
//
// Two custom controls the registry asks for by name (`x-clavian-control`), so they keep everything the generic renderer
// gives a setting — its source badge, reset to inherited, the 412 flow, audit on save:
//
//   topupPresets   `wallet.topupPresets` — the amounts the kiosk offers, as rupee chips with an inline add;
//   topupOffers    `wallet.topupOffers` — the extras, one row each, "Top up at least ₹X · Gets ₹Y extra", added and edited
//                  in a small dialog; the tier's id is made for it (a top-up names it), never typed.
//
// And a preview under the form (`TopUpPreview`, the form's `aside`): the tiles the kiosk will show, from the values on the
// screen — saved or not — by the same functions the server builds the kiosk's shop and credits a bonus with
// (`@clavian/domain/topup-offers`, imported by its light sub-path: no Zod, no `eval` under the console's CSP). A preset
// outside the smallest and largest top-up is named, since the kiosk leaves it out. Money is integer minor units on the
// wire, rupees on the screen (`SettingMoney`).

import { bestValueTileIndex, topUpPresetAmounts, topUpTiles, type TopUpTier } from '@clavian/domain/topup-offers';
import { useTranslation } from '@clavian/i18n/react';
import { Badge, Button, ChipListEditor, CloseIcon, Dialog, ListCard, ListRow, TextField, type ChipParse } from '@clavian/ui-web';
import { useState, type FormEvent, type ReactNode } from 'react';

import { parseMajorAmount, type SettingMoney } from './money';
import type { ControlProps } from './types';

const isAmount = (value: unknown): value is number => typeof value === 'number' && Number.isSafeInteger(value);

interface OfferValue {
  readonly id: string;
  readonly minTopupAmount: number;
  readonly bonusAmount: number;
}

function offersOf(value: unknown): OfferValue[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (entry): entry is OfferValue =>
      typeof entry === 'object' && entry !== null && typeof (entry as OfferValue).id === 'string' && isAmount((entry as OfferValue).minTopupAmount) && isAmount((entry as OfferValue).bonusAmount),
  );
}

/** A fresh tier id the list does not have yet: `extra-1`, `extra-2`, … (a top-up names the tier by it). */
export function freshOfferId(existing: readonly { readonly id: string }[]): string {
  const taken = new Set(existing.map((entry) => entry.id));
  let n = existing.length + 1;
  while (taken.has(`extra-${n}`)) n += 1;
  return `extra-${n}`;
}

/**
 * `wallet.topupPresets`: the amounts the PC offers, as rupee chips with an inline add (the collection pattern,
 * 2026-10-07) — typed in rupees, kept in paise, cheapest first.
 */
export function TopUpPresetsControl(props: ControlProps): ReactNode {
  const { t } = useTranslation('console');
  const list: number[] = Array.isArray(props.value) ? props.value.filter(isAmount) : [];
  const max = typeof props.schema['maxItems'] === 'number' ? props.schema['maxItems'] : 12;
  const money = props.context.money;
  const parse = (text: string): ChipParse<number> => {
    if (money === undefined) {
      const whole = Number(text);
      return Number.isSafeInteger(whole) && whole >= 1 ? { ok: true, value: whole } : { ok: false, message: t('settings.control.moneyInvalid') };
    }
    const parsed = parseMajorAmount(text, money);
    if (!parsed.ok) {
      return { ok: false, message: parsed.reason === 'fraction' ? t('settings.control.moneyFraction', { count: money.digits }) : t('settings.control.moneyInvalid') };
    }
    return parsed.minor >= 1 ? { ok: true, value: parsed.minor } : { ok: false, message: t('settings.control.moneyInvalid') };
  };
  return (
    <ChipListEditor<number>
      label={props.label}
      description={props.description}
      error={props.errorAt(props.path)}
      values={list}
      onChange={props.onChange}
      format={(amount) => (money === undefined ? String(amount) : money.format(amount))}
      parse={parse}
      inputLabel={t('settings.topUp.presetInput')}
      placeholder={money === undefined ? undefined : `${money.symbol} 500`}
      inputMode="decimal"
      emptyText={t('settings.topUp.presetsEmpty')}
      max={max}
      sort={(a, b) => a - b}
      disabled={props.disabled}
    />
  );
}

/**
 * `wallet.topupOffers`: the extras, one readable row each — "Top up at least ₹500 · Gets ₹50 extra" — added and edited
 * in a small centred dialog; the tier id is made for it (a top-up names the tier by it), never typed.
 */
export function TopUpOffersControl(props: ControlProps): ReactNode {
  const { t } = useTranslation('console');
  const list: unknown[] = Array.isArray(props.value) ? props.value : [];
  const max = typeof props.schema['maxItems'] === 'number' ? props.schema['maxItems'] : 20;
  const offers = offersOf(list);
  const money = props.context.money;
  const show = (minor: number) => (money === undefined ? String(minor) : money.format(minor));
  const [editing, setEditing] = useState<{ readonly index: number | null } | null>(null);
  const current = editing?.index == null ? undefined : offers[editing.index];

  const save = (minTopupAmount: number, bonusAmount: number) => {
    if (editing === null) return;
    const next =
      editing.index === null
        ? [...list, { id: freshOfferId(offers), minTopupAmount, bonusAmount }]
        : list.map((existing, at) => (at === editing.index ? { ...(existing as Record<string, unknown>), minTopupAmount, bonusAmount } : existing));
    props.onChange(next);
    setEditing(null);
  };

  const addButton =
    props.disabled || list.length >= max ? undefined : (
      <Button size="sm" variant="secondary" onClick={() => setEditing({ index: null })}>
        {t('settings.topUp.addOffer')}
      </Button>
    );

  return (
    <div className="settings-topup">
      <ListCard
        className="console-list-card--embedded"
        title={props.label}
        summary={offers.length === 0 ? props.description : t('settings.topUp.offersSummary', { count: offers.length })}
        count={list.length}
        listLabel={props.label}
        emptyText={t('settings.topUp.offersEmpty')}
        {...(addButton === undefined ? {} : { action: addButton })}
      >
        {list.map((offer, index) => {
          const value = (typeof offer === 'object' && offer !== null ? offer : {}) as Partial<OfferValue>;
          const error = props.errorAt([...props.path, index, 'minTopupAmount']) ?? props.errorAt([...props.path, index, 'bonusAmount']);
          return (
            <ListRow
              key={value.id ?? index}
              title={t('settings.topUp.offerTitle', { min: isAmount(value.minTopupAmount) ? show(value.minTopupAmount) : '—' })}
              meta={t('settings.topUp.offerMeta', { bonus: isAmount(value.bonusAmount) ? show(value.bonusAmount) : '—' })}
              {...(props.disabled
                ? {}
                : {
                    actions: (
                      <>
                        <Button size="sm" variant="ghost" aria-label={t('settings.topUp.editOffer', { number: index + 1 })} onClick={() => setEditing({ index })}>
                          {t('settings.holidays.editShort')}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          icon={<CloseIcon />}
                          label={t('settings.topUp.removeOffer', { number: index + 1 })}
                          onClick={() => props.onChange(list.filter((_, at) => at !== index))}
                        />
                      </>
                    ),
                  })}
            >
              {error === undefined ? null : <p className="clav-field__error">{error}</p>}
            </ListRow>
          );
        })}
      </ListCard>
      <Dialog
        open={editing !== null}
        onOpenChange={(open) => (open ? undefined : setEditing(null))}
        size="sm"
        dirty="auto"
        title={editing?.index == null ? t('settings.topUp.offerAddTitle') : t('settings.topUp.offerEditTitle')}
        description={props.description}
      >
        {editing === null ? null : (
          <OfferForm
            start={current}
            {...(money === undefined ? {} : { money })}
            onCancel={() => setEditing(null)}
            onSave={save}
          />
        )}
      </Dialog>
    </div>
  );
}

function OfferForm({
  start,
  money,
  onCancel,
  onSave,
}: {
  readonly start: OfferValue | undefined;
  readonly money?: SettingMoney;
  readonly onCancel: () => void;
  readonly onSave: (minTopupAmount: number, bonusAmount: number) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const shown = (minor: number | undefined) => (minor === undefined ? '' : money === undefined ? String(minor) : money.formatInput(minor));
  const [least, setLeast] = useState(shown(start?.minTopupAmount ?? 50_000));
  const [bonus, setBonus] = useState(shown(start?.bonusAmount ?? 5_000));
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const parse = (text: string): number | undefined => {
    if (money === undefined) {
      const whole = Number(text);
      return Number.isSafeInteger(whole) && whole >= 0 ? whole : undefined;
    }
    const parsed = parseMajorAmount(text, money);
    return parsed.ok ? parsed.minor : undefined;
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const leastMinor = parse(least);
    const bonusMinor = parse(bonus);
    if (leastMinor === undefined || bonusMinor === undefined) {
      setProblem(t('settings.topUp.offerInvalid'));
      return;
    }
    onSave(leastMinor, bonusMinor);
  };
  const symbol = money?.symbol;
  return (
    <form className="console-form settings-topup__form" noValidate onSubmit={submit} aria-label={t('settings.topUp.offerAddTitle')}>
      <TextField
        label={symbol === undefined ? t('settings.topUp.offerMinField') : `${t('settings.topUp.offerMinField')} (${symbol})`}
        required
        numeric
        inputMode="decimal"
        value={least}
        onChange={(event) => setLeast(event.target.value)}
      />
      <TextField
        label={symbol === undefined ? t('settings.topUp.offerBonusField') : `${t('settings.topUp.offerBonusField')} (${symbol})`}
        required
        numeric
        inputMode="decimal"
        value={bonus}
        onChange={(event) => setBonus(event.target.value)}
        error={problem}
      />
      <div className="console-form__actions console-form__actions--end">
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="primary">
          {t('settings.topUp.offerSave')}
        </Button>
      </div>
    </form>
  );
}

/** The custom controls of the Payments group, one stable set. */
export const TOPUP_CONTROLS = { topupPresets: TopUpPresetsControl, topupOffers: TopUpOffersControl } as const;

/** The settings keys the preview reads. */
export const TOPUP_KEYS = ['wallet.topupPresets', 'wallet.topupOffers', 'wallet.minTopupAmount', 'wallet.maxTopupAmount'] as const;

export interface TopUpPreviewProps {
  /** The value on the screen for a key — the draft, else what is stored — or `undefined` when the screen has no such entry. */
  readonly valueOf: (key: string) => unknown;
  readonly money: SettingMoney;
}

/** What the kiosk will show: the tiles, cheapest first, each with its extra; then "Other amount". */
export function TopUpPreview({ valueOf, money }: TopUpPreviewProps): ReactNode {
  const { t } = useTranslation('console');
  const min = valueOf('wallet.minTopupAmount');
  const max = valueOf('wallet.maxTopupAmount');
  const range = { minMinor: isAmount(min) ? min : 0, maxMinor: isAmount(max) ? max : Number.MAX_SAFE_INTEGER };
  const known = isAmount(min) && isAmount(max);
  const raw = valueOf('wallet.topupPresets');
  const presets = Array.isArray(raw) ? raw.filter(isAmount) : [];
  const tiers: TopUpTier[] = offersOf(valueOf('wallet.topupOffers')).map((offer) => ({ id: offer.id, minTopupMinor: offer.minTopupAmount, bonusMinor: offer.bonusAmount }));
  // Without the limits on this screen the derived default cannot be drawn; the venue's own presets still can.
  const tiles = known || presets.length > 0 ? topUpTiles(range, presets, tiers) : [];
  const shown = new Set(topUpPresetAmounts(range, presets, tiers));
  const outside = known ? presets.filter((amount) => !shown.has(amount) && (amount < range.minMinor || amount > range.maxMinor)) : [];
  const best = bestValueTileIndex(tiles.map((tile) => ({ amountMinor: tile.amountMinor, bonusMinor: tile.tier?.bonusMinor ?? 0 })));

  return (
    <section className="settings-topup-preview" aria-labelledby="settings-topup-preview-title">
      <h3 id="settings-topup-preview-title" className="settings-topup-preview__title">
        {t('settings.topUp.previewTitle')}
      </h3>
      <p className="console-muted">{presets.length > 0 ? t('settings.topUp.previewPresets') : t('settings.topUp.previewDerived')}</p>
      <ul className="settings-topup-preview__tiles">
        {tiles.map((tile, index) => (
          <li key={tile.amountMinor} className="settings-topup-preview__tile" data-bonus={tile.tier !== null ? true : undefined}>
            <span className="settings-topup-preview__amount">{money.format(tile.amountMinor)}</span>
            {tile.tier !== null ? (
              <Badge tone="accent">{t('settings.topUp.extra', { bonus: money.format(tile.tier.bonusMinor) })}</Badge>
            ) : (
              <span className="console-muted">{t('settings.topUp.noExtra')}</span>
            )}
            {index === best ? <Badge tone="ok">{t('settings.topUp.bestValue')}</Badge> : null}
          </li>
        ))}
        <li className="settings-topup-preview__tile settings-topup-preview__tile--other">
          <span className="settings-topup-preview__amount">{t('settings.topUp.otherAmount')}</span>
          <span className="console-muted">
            {known ? t('settings.topUp.otherHint', { min: money.format(range.minMinor), max: money.format(range.maxMinor) }) : t('settings.topUp.otherHintPlain')}
          </span>
        </li>
      </ul>
      {outside.length > 0 ? (
        <p className="settings-topup-preview__warn" role="status">
          {t('settings.topUp.outside', {
            amounts: outside.map((amount) => money.format(amount)).join(', '),
            min: money.format(range.minMinor),
            max: money.format(range.maxMinor),
          })}
        </p>
      ) : null}
    </section>
  );
}
